/**
 * PersonalAuditLog — T26.
 * Shows the current user's own audit events.
 * GET /api/audit-events/me?limit=20&offset=... (paginated).
 */
import { Alert, Button, Group, Stack, Table, Text, Title } from '@mantine/core';
import { useEffect, useState } from 'react';
import { auditEventsApi } from '../../api/endpoints/audit-events';

interface AuditEvent {
  id: string;
  eventType: string;
  entityType?: string | null;
  entityId?: string | null;
  payload: Record<string, unknown>;
  createdAt: string;
}

const PAGE_SIZE = 20;

export default function PersonalAuditLog() {
  const [events, setEvents] = useState<AuditEvent[]>([]);
  const [total, setTotal] = useState(0);
  const [offset, setOffset] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  async function fetchPage(pageOffset: number) {
    setLoading(true);
    setError(null);
    try {
      const res = (await auditEventsApi.listMine({ limit: PAGE_SIZE, offset: pageOffset })) as {
        ok: true;
        events?: AuditEvent[];
        items?: AuditEvent[];
        total: number;
      };
      setEvents(res.events ?? res.items ?? []);
      setTotal(res.total);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load audit log.');
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    void fetchPage(0);
  }, []);

  function handlePageChange(newOffset: number) {
    setOffset(newOffset);
    void fetchPage(newOffset);
  }

  const currentPage = Math.floor(offset / PAGE_SIZE) + 1;
  const totalPages = Math.ceil(total / PAGE_SIZE) || 1;

  return (
    <Stack gap="sm" data-testid="personal-audit-log">
      <Title order={5}>My activity</Title>

      {error && (
        <Alert color="red" data-testid="audit-log-error">
          {error}
        </Alert>
      )}

      {loading && (
        <Text c="dimmed" size="sm">
          Loading…
        </Text>
      )}

      {!loading && events.length === 0 && (
        <Text c="dimmed" size="sm" data-testid="audit-log-empty">
          No activity yet.
        </Text>
      )}

      {!loading && events.length > 0 && (
        <>
          <Table striped highlightOnHover withTableBorder withColumnBorders>
            <Table.Thead>
              <Table.Tr>
                <Table.Th>Event</Table.Th>
                <Table.Th>When</Table.Th>
              </Table.Tr>
            </Table.Thead>
            <Table.Tbody>
              {events.map((ev) => (
                <Table.Tr key={ev.id} data-testid={`audit-row-${ev.id}`}>
                  <Table.Td>
                    <Text size="sm" ff="monospace">
                      {ev.eventType}
                    </Text>
                    {ev.entityType && (
                      <Text size="xs" c="dimmed">
                        {ev.entityType}
                        {ev.entityId ? ` / ${ev.entityId}` : ''}
                      </Text>
                    )}
                  </Table.Td>
                  <Table.Td>
                    <Text size="sm">{new Date(ev.createdAt).toLocaleString()}</Text>
                  </Table.Td>
                </Table.Tr>
              ))}
            </Table.Tbody>
          </Table>

          <Group justify="space-between" mt="xs">
            <Text size="sm" c="dimmed">
              Page {currentPage} of {totalPages} ({total} total)
            </Text>
            <Group gap="xs">
              <Button
                size="xs"
                variant="default"
                disabled={offset === 0}
                onClick={() => {
                  handlePageChange(Math.max(0, offset - PAGE_SIZE));
                }}
                data-testid="audit-log-prev"
              >
                Previous
              </Button>
              <Button
                size="xs"
                variant="default"
                disabled={offset + PAGE_SIZE >= total}
                onClick={() => {
                  handlePageChange(offset + PAGE_SIZE);
                }}
                data-testid="audit-log-next"
              >
                Next
              </Button>
            </Group>
          </Group>
        </>
      )}
    </Stack>
  );
}
